import { useEffect, useRef } from 'react';
import {
  getValidToken,
  getPlaybackState,
  getCurrentlyPlaying,
  getQueue,
  getAudioFeatures,
  getAudioAnalysis,
} from '../../Strobe/spotify.js';
import { PlaybackClock, normalizeQueueItem, normalizeSpotifyPlayback } from '../engine/playbackClock';
import { computeBeatState, estimateBpmFromTap } from '../engine/beatSystem';
import { activeVisualEvent } from '../engine/timeline';
import { lyricForDisplay } from '../engine/lyricsApi';
import { enrichTrackLyrics, getCachedTrack, prepareTrack } from '../engine/trackPreparation';
import { makeManualAudioProfile, makeSpotifyAudioProfile } from '../engine/spotifyAudioProfile';
import { usePartyStore } from '../store/partyStore';
import type { QueueTrackPreview, TrackAudioProfile } from '../types';

const POLL_MS = 500;
const MAX_AUDIO_PROFILE_CACHE_ENTRIES = 64;
const audioProfileCache = new Map<string, TrackAudioProfile>();
const queuePreloadInflight = new Set<string>();

function trimAudioProfileCache() {
  while (audioProfileCache.size > MAX_AUDIO_PROFILE_CACHE_ENTRIES) {
    const oldestKey = audioProfileCache.keys().next().value;
    if (!oldestKey) break;
    audioProfileCache.delete(oldestKey);
  }
}

export function useSpotifyPartySync(clientId: string, enabled: boolean) {
  const clockRef = useRef(new PlaybackClock());
  const beatIndexRef = useRef(-1);
  const rafRef = useRef<number | undefined>(undefined);
  const lastTrackIdRef = useRef<string | null>(null);
  const lastProfileKeyRef = useRef<string | null>(null);

  const {
    setPlayback,
    setCurrentTrack,
    setNextTrack,
    setQueueTracks,
    markTrackPrepared,
    setSpotifyDevice,
    setSyncStatus,
    setActiveEvent,
    setDisplayLyric,
    setLyricsStatus,
    visualStyle,
    manualBpm,
  } = usePartyStore();

  useEffect(() => {
    if (!enabled || !clientId) return;
    let alive = true;

    async function poll() {
      if (!alive) return;
      try {
        setSyncStatus('polling');
        const token = await getValidToken(clientId);
        if (!token) {
          setSyncStatus('stale');
          return;
        }

        const raw =
          (await getPlaybackState(token)) ?? (await getCurrentlyPlaying(token));
        const snapshot = normalizeSpotifyPlayback(raw);
        if (!snapshot?.trackId) {
          setSyncStatus('stale');
          return;
        }

        clockRef.current.sync(snapshot);
        setSpotifyDevice(snapshot.deviceName, snapshot.deviceType);
        setSyncStatus('synced');

        const profileKey = manualBpm ? `manual:${manualBpm}` : 'spotify';
        if (snapshot.trackId !== lastTrackIdRef.current || profileKey !== lastProfileKeyRef.current) {
          lastTrackIdRef.current = snapshot.trackId;
          lastProfileKeyRef.current = profileKey;
          beatIndexRef.current = -1;
          setLyricsStatus('loading');

          const audioProfile = manualBpm
            ? makeManualAudioProfile(manualBpm)
            : await getSpotifyAudioProfile(snapshot.trackId, token, true);

          const track = await prepareTrack(
            {
              id: snapshot.trackId,
              title: snapshot.title,
              artist: snapshot.artist,
              album: snapshot.album,
              durationMs: snapshot.durationMs,
              albumArtUrl: snapshot.albumArtUrl,
              audioProfile,
            },
            visualStyle,
            { lazy: false }
          );

          setCurrentTrack(track);
          markTrackPrepared(track.spotifyTrackId);
          setLyricsStatus(track.hasLyrics ? 'synced' : 'missing');

          if (!track.hasLyrics) {
            enrichTrackLyrics(track).then((enriched) => {
              if (!alive || enriched.spotifyTrackId !== lastTrackIdRef.current) return;
              setCurrentTrack(enriched);
              setLyricsStatus(enriched.hasLyrics ? 'synced' : 'missing');
            });
          }
        }

        const queueData = await getQueue(token);
        const queueItems = queueData?.queue as Record<string, unknown>[] | undefined;
        if (queueItems?.length) {
          const previews: QueueTrackPreview[] = queueItems.slice(0, 8).map((item) => {
            const n = normalizeQueueItem(item);
            return {
              id: n.id,
              title: n.title,
              artist: n.artist,
              albumArtUrl: n.albumArtUrl,
              durationMs: n.durationMs,
              prepared: Boolean(getCachedTrack(n.id)),
            };
          });
          setNextTrack(previews[0] ?? null);
          setQueueTracks(previews);

          const markPreparedIfAlive = (prepared: { spotifyTrackId: string }) => {
            if (!alive) return;
            markTrackPrepared(prepared.spotifyTrackId);
          };

          for (const item of queueItems.slice(0, 6)) {
            const n = normalizeQueueItem(item);
            if (getCachedTrack(n.id)) {
              markTrackPrepared(n.id);
              continue;
            }
            const preloadKey = `${n.id}:${visualStyle}:${manualBpm ?? 'spotify'}`;
            if (queuePreloadInflight.has(preloadKey)) continue;
            queuePreloadInflight.add(preloadKey);
            try {
              const audioProfile = manualBpm
                ? makeManualAudioProfile(manualBpm)
                : await getSpotifyAudioProfile(n.id, token);
              prepareTrack(
                {
                  id: n.id,
                  title: n.title,
                  artist: n.artist,
                  album: '',
                  durationMs: n.durationMs,
                  albumArtUrl: n.albumArtUrl,
                  audioProfile,
                },
                visualStyle
              )
                .then(markPreparedIfAlive)
                .catch(() => {})
                .finally(() => {
                  queuePreloadInflight.delete(preloadKey);
                });
            } catch {
              queuePreloadInflight.delete(preloadKey);
            }
          }
        } else {
          setNextTrack(null);
          setQueueTracks([]);
        }
      } catch {
        setSyncStatus('error');
        // keep last known state
      }
    }

    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [
    enabled,
    clientId,
    visualStyle,
    manualBpm,
    setCurrentTrack,
    setNextTrack,
    setQueueTracks,
    markTrackPrepared,
    setSpotifyDevice,
    setSyncStatus,
    setLyricsStatus,
  ]);

  useEffect(() => {
    if (!enabled) return;

    const tick = () => {
      rafRef.current = requestAnimationFrame(tick);
      const state = usePartyStore.getState();
      const progressMs = clockRef.current.getProgressMs();
      const isPlaying = clockRef.current.getIsPlaying();
      const track = state.currentTrack;
      const { overrides, manualBpm, tapTimes } = state;

      const bpm =
        manualBpm ??
        (tapTimes.length >= 2 ? estimateBpmFromTap(tapTimes) : track?.bpm ?? 128);

      const beat = computeBeatState(
        progressMs,
        {
          bpm,
          manualTapTimes: tapTimes,
          beatOffsetMs: manualBpm || tapTimes.length >= 2 ? 0 : track?.audioProfile.beatOffsetMs,
        },
        beatIndexRef.current
      );
      if (beat.isBeat) beatIndexRef.current = beat.beatIndex;

      let activeEvent = track
        ? activeVisualEvent(track.visualTimeline, progressMs)
        : null;

      if (state.sceneOverride && progressMs < state.sceneOverride.endMs) {
        activeEvent = state.sceneOverride;
      } else if (state.sceneOverride && progressMs >= state.sceneOverride.endMs) {
        usePartyStore.setState({ sceneOverride: null });
      }

      if (activeEvent && overrides.sceneLocked && overrides.forcedSceneId && activeEvent.sceneId !== overrides.forcedSceneId) {
        activeEvent = { ...activeEvent, sceneId: overrides.forcedSceneId };
      }

      let displayLyric = '';
      if (track && overrides.lyricsEnabled !== false) {
        displayLyric = lyricForDisplay(
          track.lyrics,
          track.hooks,
          progressMs,
          overrides.lyricDisplayMode,
          overrides.forceLyricMode
        );
      }

      setPlayback(progressMs, isPlaying, beat);
      setActiveEvent(activeEvent);
      setDisplayLyric(displayLyric);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, setPlayback, setActiveEvent, setDisplayLyric]);
}

export async function getSpotifyAudioProfile(trackId: string, token: string, includeAnalysis = false): Promise<TrackAudioProfile> {
  const cached = audioProfileCache.get(trackId);
  if (cached && (!includeAnalysis || cached.beatOffsetMs > 0 || cached.bpmSource === 'spotify-analysis')) return cached;

  const features = await getAudioFeatures(trackId, token);
  const analysis = includeAnalysis || !features?.tempo ? await getAudioAnalysis(trackId, token) : null;
  const profile = makeSpotifyAudioProfile(features, analysis);
  if (profile.bpmSource !== 'fallback') {
    audioProfileCache.set(trackId, profile);
    trimAudioProfileCache();
  }
  return profile;
}
